/**
 * ArtemisMissionStages Component
 * Displays clickable hotspots at specific points along the Artemis trajectory
 * with tooltip functionality
 */

import { useMemo, useRef, useEffect, useState } from 'react'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'

// Reusable temporaries for the per-frame occlusion test (avoid per-marker GC).
const _markerWorld = new THREE.Vector3()
const _toMarker = new THREE.Vector3()
const _toCentre = new THREE.Vector3()
const _closest = new THREE.Vector3()

// True if `point` is hidden from `camPos` by any occluder sphere — i.e. the
// camera→point ray passes within an occluder's radius AND that occluder lies
// between the camera and the point (not behind the point). Analytic sphere test,
// far cheaper than raycasting and exact for the spherical planetoids.
function isOccluded(point, camPos, occluders) {
  if (!occluders) return false
  _toMarker.subVectors(point, camPos)
  const distToMarker = _toMarker.length()
  if (distToMarker < 1e-4) return false
  _toMarker.divideScalar(distToMarker) // normalised ray dir
  for (let i = 0; i < occluders.length; i++) {
    const o = occluders[i]
    _toCentre.set(o.center[0], o.center[1], o.center[2]).sub(camPos)
    const proj = _toCentre.dot(_toMarker) // distance along ray to closest approach
    // Sphere must be in front of the camera and nearer than the marker.
    if (proj <= 0 || proj >= distToMarker) continue
    _closest.copy(_toMarker).multiplyScalar(proj).add(camPos)
    const dx = _closest.x - o.center[0]
    const dy = _closest.y - o.center[1]
    const dz = _closest.z - o.center[2]
    if (dx * dx + dy * dy + dz * dz < o.radius * o.radius) return true
  }
  return false
}

// Converts a '#rrggbb' hex color to 'rgba(r, g, b, alpha)'.
function hexToRgba(hex, alpha) {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function MissionStageMarker({
  controlPoints,
  trajectoryPercent,
  stageName,
  stageNumber,
  stageId,
  dotColor = '#EE3A17',
  dotShape = 'circle',
  bracketColor = '#E3E3E3',
  textColor = '#E3E3E3',
  blink = 1,
  reached = false,
  onClick,
  onPositionUpdate,
  isActive = false,
  launched = false,
  visible = true,
  visited = false,
  occluders = null,
  isFocused = false,
  focusImageSrc = null,
  focusCaption = null,
}) {
  const markerRef = useRef(null)
  const groupRef = useRef(null)
  const occludedRef = useRef(false)
  const { camera } = useThree()
  const [rendered, setRendered] = useState(false)
  const [glitchClass, setGlitchClass] = useState('')
  const enterDelay = ((trajectoryPercent * 17 + 31) % 100) / 100 * 1600
  const glitchDuration = `${1.0 + ((trajectoryPercent * 11) % 100) / 100 * 0.7}s`
  const enterTimer = useRef(null)
  const exitTimer = useRef(null)
  const glitchRaf = useRef(null)
  const glitchTimer = useRef(null)

  useEffect(() => {
    if (visible) {
      clearTimeout(exitTimer.current)
      enterTimer.current = setTimeout(() => {
        setRendered(true)
        glitchRaf.current = requestAnimationFrame(() => {
          setGlitchClass('marker-glitch-in')
          glitchTimer.current = setTimeout(() => setGlitchClass(''), 300)
        })
      }, enterDelay)
    } else {
      clearTimeout(enterTimer.current)
      if (rendered) {
        setGlitchClass('marker-glitch-out')
        exitTimer.current = setTimeout(() => {
          setRendered(false)
          setGlitchClass('')
        }, 250)
      }
    }
    return () => {
      clearTimeout(enterTimer.current)
      clearTimeout(exitTimer.current)
      clearTimeout(glitchTimer.current)
      cancelAnimationFrame(glitchRaf.current)
    }
  }, [visible])

  const curve = useMemo(() => {
    if (!controlPoints || controlPoints.length < 2) return null
    const curvePath = new THREE.CurvePath()
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const p0 = controlPoints[i]
      const p1 = controlPoints[i + 1]
      const anchor0 = new THREE.Vector3(...p0.pos)
      const anchor1 = new THREE.Vector3(...p1.pos)
      const handle0Out = new THREE.Vector3(...p0.pos).add(new THREE.Vector3(...p0.handleOut))
      const handle1In = new THREE.Vector3(...p1.pos).add(new THREE.Vector3(...p1.handleIn))
      curvePath.add(new THREE.CubicBezierCurve3(anchor0, handle0Out, handle1In, anchor1))
    }
    return curvePath
  }, [controlPoints])

  const position = useMemo(() => {
    if (!curve) return [0, 0, 0]
    const point = curve.getPoint(trajectoryPercent / 100)
    return [point.x, point.y, point.z]
  }, [curve, trajectoryPercent])

  useFrame(() => {
    // Hide the marker when a planetoid (Earth/Moon) sits between it and the
    // camera — otherwise side-on views jumble all the markers together.
    if (rendered && markerRef.current && groupRef.current && occluders) {
      groupRef.current.getWorldPosition(_markerWorld)
      const occ = isOccluded(_markerWorld, camera.position, occluders)
      if (occ !== occludedRef.current) {
        occludedRef.current = occ
        markerRef.current.style.visibility = occ ? 'hidden' : 'visible'
        markerRef.current.style.pointerEvents = occ ? 'none' : (onClick ? 'auto' : 'none')
      }
    }
    if (rendered && isActive && markerRef.current && onPositionUpdate) {
      const rect = markerRef.current.getBoundingClientRect()
      onPositionUpdate(stageId, rect.left + rect.width / 2, rect.top + rect.height / 2)
    }
  })

  if (!curve || !rendered) return null

  const idleBackground = reached ? '#E3E3E3' : (visited ? hexToRgba(dotColor, 0.5) : '#000000')
  const idleBorderColor = reached ? '#E3E3E3' : (visited ? hexToRgba(dotColor, 0.5) : dotColor)
  const showFocusBox = isFocused && !!focusImageSrc

  if (showFocusBox) {
    return (
      <group ref={groupRef} position={position}>
        <Html key="f" sprite zIndexRange={[0, 0]} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          <div
            ref={markerRef}
            className={`mission-stage-focus-box ${glitchClass}`}
            onClick={(e) => {
              if (onClick) {
                const rect = e.currentTarget.getBoundingClientRect()
                onClick(stageId, stageName, rect.left + rect.width / 2, rect.top + rect.height / 2, e)
              }
            }}
            style={{
              border: `2px solid ${dotColor}`,
              animationDuration: glitchDuration,
              cursor: onClick ? 'pointer' : 'default',
              pointerEvents: onClick ? 'auto' : 'none',
            }}
          >
            {stageNumber && (
              <div
                className={`mission-stage-focus-box-badge${dotShape === 'circle' ? ' focus-box-badge--circle' : ''}`}
                style={{
                  backgroundColor: dotColor,
                  borderRadius: dotShape === 'diamond' ? '2px' : '50%',
                  transform: dotShape === 'diamond' ? 'rotate(45deg)' : 'none',
                }}
              >
                <span style={{ transform: dotShape === 'diamond' ? 'rotate(-45deg)' : 'none' }}>
                  {stageNumber}
                </span>
              </div>
            )}
            <img src={focusImageSrc} alt={stageName} className="mission-stage-focus-box-image" />
            {focusCaption && (
              <span className="mission-stage-focus-box-caption">{focusCaption}</span>
            )}
          </div>
        </Html>
      </group>
    )
  }

  return (
    <group ref={groupRef} position={position}>
      <Html key="n" sprite zIndexRange={[0, 0]} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <div
          ref={markerRef}
          className={`mission-stage-marker ${glitchClass}`}
          onClick={(e) => {
            if (onClick) {
              const rect = e.currentTarget.getBoundingClientRect()
              onClick(stageId, stageName, rect.left + rect.width / 2, rect.top + rect.height / 2, e)
            }
          }}
          style={{
            width: launched ? '10px' : (dotShape === 'diamond' ? '24px' : '36px'),
            height: launched ? '10px' : (dotShape === 'diamond' ? '24px' : '36px'),
            borderRadius: dotShape === 'diamond' ? '2px' : '50%',
            backgroundColor: idleBackground,
            border: `2px solid ${idleBorderColor}`,
            boxShadow: `0 0 ${launched ? '10px' : '36px'} ${reached ? '#E3E3E3' : dotColor}`,
            transform: dotShape === 'diamond'
              ? 'translate(-50%, -50%) rotate(45deg)'
              : 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'monospace',
            color: reached ? '#000000' : '#E3E3E3',
            cursor: onClick ? 'pointer' : 'default',
            pointerEvents: onClick ? 'auto' : 'none',
            animationDuration: glitchDuration,
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            if (!onClick) return
            e.currentTarget.style.transform = `translate(-50%, -50%)${dotShape === 'diamond' ? ' rotate(45deg)' : ''} scale(1.1)`
            e.currentTarget.style.backgroundColor = reached ? '#E3E3E3' : dotColor
            e.currentTarget.style.borderColor = reached ? '#E3E3E3' : dotColor
          }}
          onMouseLeave={(e) => {
            if (!onClick) return
            e.currentTarget.style.transform = `translate(-50%, -50%)${dotShape === 'diamond' ? ' rotate(45deg)' : ''}`
            e.currentTarget.style.backgroundColor = idleBackground
            e.currentTarget.style.borderColor = idleBorderColor
          }}
        >
          <span style={{
            display: 'inline-block',
            transform: dotShape === 'diamond' ? 'rotate(-45deg)' : undefined,
            fontSize: dotShape === 'diamond' ? '7px' : undefined,
            whiteSpace: 'nowrap',
            letterSpacing: '0.5px',
            fontWeight: '400',
            color: reached ? '#000000' : '#E3E3E3',
            opacity: (!launched && stageNumber) ? 1 : 0,
            pointerEvents: 'none',
          }}>
            {(!launched && stageNumber) ? stageNumber : ''}
          </span>
        </div>
      </Html>
    </group>
  )
}

export default MissionStageMarker
